'use client';

import React, { useEffect, useRef } from 'react';

interface MenuItem {
  id: number;
  name: string;
  nameLocal: string;
  description: string;
  detail: string;
  tag: string;
  image: string;
  alt: string;
}

const menuItems: MenuItem[] = [
{
  id: 1,
  name: 'Nems & Samossas',
  nameLocal: 'Entrées Croustillantes',
  description: 'Nems au porc et samossas aux légumes, faits maison chaque matin. Servis avec sauce aigre-douce et sauce piment.',
  detail: 'Fait maison chaque jour',
  tag: 'Signature',
  image: "/assets/images/menu-nems.jpg",
  alt: 'Assortiment de nems et samossas croustillants'
},
{
  id: 2,
  name: 'Bo Bun',
  nameLocal: 'Bo Bun Vietnamien',
  description: 'Vermicelles de riz, bœuf sauté aux saveurs asiatiques, crudités fraîches, cacahuètes et herbes aromatiques.',
  detail: 'Recette traditionnelle',
  tag: 'Plat',
  image: "/assets/images/menu-bobun.jpg",
  alt: 'Bo bun vietnamien avec bœuf et vermicelles'
},
{
  id: 3,
  name: 'Riz Cantonais',
  nameLocal: 'Riz Sauté aux Œufs',
  description: 'Riz sauté au wok avec œufs, petits pois, jambon et oignons verts. Accompagnement parfait pour tous les plats.',
  detail: 'Cuit au wok à la commande',
  tag: 'Accompagnement',
  image: "/assets/images/menu-rizcantonais.jpg",
  alt: 'Riz cantonais sauté au wok'
},
{
  id: 4,
  name: 'Plateau de Sushis',
  nameLocal: 'Assortiment Maki',
  description: 'Assortiment de makis fraîchement préparés: saumon, thon, avocat et légumes. Riz vinaigré à la japonaise.',
  detail: 'Préparé chaque matin',
  tag: 'Buffet',
  image: "/assets/images/menu-sushis.jpg",
  alt: 'Plateau de sushis et makis variés'
}];


const MenuSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = entry.target.querySelectorAll('.reveal-on-scroll');
            reveals.forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="menu" ref={sectionRef} className="py-32 bg-parchment relative overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }} />
      

      {/* Marquee — dish names */}
      <div className="overflow-hidden mb-16 border-y border-clay/15 py-4">
        <div className="animate-marquee-slow">
          {[...Array(2)].map((_, pass) =>
          <div key={pass} className="flex items-center gap-12 px-6">
              {['Nems', '·', 'Sushis', '·', 'Bo Bun', '·', 'Riz', '·', 'Samossas', '·', 'Wok', '·'].map((word, i) =>
            <span
              key={i}
              className={`whitespace-nowrap font-serif italic text-2xl ${
              word === '·' ? 'text-clay text-sm' : 'text-charcoal/30'}`
              }>

                  {word}
                </span>
            )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
          <div>
            <p className="text-clay text-xs uppercase tracking-widest font-sans font-medium mb-4">
              La Carte
            </p>
            <h2
              className="font-serif italic text-charcoal leading-tight"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>

              Tout est fait<br />
              <span className="text-gradient-warm">maison.</span>
            </h2>
          </div>
          <a
            href="#booking"
            className="text-charcoal/50 text-sm uppercase tracking-widest font-medium border-b border-charcoal/20 pb-0.5 hover:text-charcoal hover:border-charcoal/50 transition-all self-end">

            Voir la carte complète →
          </a>
        </div>

        {/* Menu Cards — asymmetric bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
          {/* Card 1 — large, spans 5 cols */}
          <div className="reveal-on-scroll lg:col-span-5 menu-card bg-charcoal rounded-4xl overflow-hidden shadow-xl shadow-charcoal/15">
            <div className="aspect-[4/3] img-zoom">
              <img
                src={menuItems[0].image}
                alt={menuItems[0].alt}
                className="w-full h-full object-cover opacity-80" />
              
            </div>
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-clay text-xs uppercase tracking-widest font-sans">
                  {menuItems[0].tag}
                </span>
                <span className="text-parchment/30 text-xs font-sans italic">
                  {menuItems[0].detail}
                </span>
              </div>
              <h3 className="font-serif italic text-parchment text-3xl mb-2">
                {menuItems[0].name}
              </h3>
              <p className="text-parchment/50 text-sm font-sans leading-relaxed">
                {menuItems[0].description}
              </p>
            </div>
          </div>

          {/* Cards 2 & 3 — stacked, spans 4 cols */}
          <div className="reveal-on-scroll lg:col-span-4 flex flex-col gap-6">
            {menuItems.slice(1, 3).map((item) =>
            <div
              key={item.id}
              className="menu-card bg-white rounded-4xl overflow-hidden shadow-lg shadow-charcoal/8 flex flex-col">
              
                <div className="h-48 img-zoom">
                  <img
                  src={item.image}
                  alt={item.alt}
                  className="w-full h-full object-cover" />
                
                </div>
                <div className="p-6 flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-corsasia text-xs uppercase tracking-widest font-sans font-medium">
                      {item.tag}
                    </span>
                  </div>
                  <h3 className="font-serif italic text-charcoal text-xl mb-1">{item.name}</h3>
                  <p className="text-charcoal/40 text-xs font-sans italic mb-2">{item.nameLocal}</p>
                  <p className="text-charcoal/60 text-sm font-sans leading-relaxed line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Card 4 — wide, spans 3 cols */}
          <div className="reveal-on-scroll lg:col-span-3 menu-card bg-parchment-dark rounded-4xl overflow-hidden shadow-lg border border-clay/20 flex flex-col">
            <div className="h-52 img-zoom">
              <img
                src={menuItems[3].image}
                alt={menuItems[3].alt}
                className="w-full h-full object-cover" />
              
            </div>
            <div className="p-6 flex-1">
              <span className="text-clay text-xs uppercase tracking-widest font-sans font-medium block mb-3">
                {menuItems[3].tag}
              </span>
              <h3 className="font-serif italic text-charcoal text-xl mb-2">{menuItems[3].name}</h3>
              <p className="text-charcoal/55 text-sm font-sans leading-relaxed">
                {menuItems[3].description}
              </p>
            </div>
          </div>
        </div>

        {/* Separator note */}
        <div className="reveal-on-scroll mt-16 text-center">
          <p className="text-charcoal/30 text-sm font-sans italic">
            Tous nos plats sont préparés frais chaque jour. Buffet à volonté sur place.
          </p>
        </div>
      </div>
    </section>);

};

export default MenuSection;